export interface CoachingFlag {
  id: string
  type:
    | 'low-calls'
    | 'low-pull-to-app'
    | 'low-coverage'
    | 'high-fallout'
    | 'stale-pipeline'
  description: string
}

export interface LoanOfficer {
  id: string
  name: string
  initials: string
  team: string
  five9CallsPerDay: number
  five9TalkTime: number
  ringCentralCallsPerDay: number
  ringCentralTalkTime: number
  conversations: number
  creditPulls: number
  applications: number
  submissions: number
  signed: number
  locks: number
  fundings: number
  pullToAppPct: number
  appToSubmissionPct: number
  submissionToLockPct: number
  submissionToSignedPct: number
  signedToFundPct: number
  lockedPipelineValue: number
  pipelineValue: number
  falloutPct: number
  followUpCompliancePct: number
  suspendedFilePct: number
  sparklineData: number[]
  coachingFlags: CoachingFlag[]
}

export interface KPI {
  id: string
  label: string
  value: number
  formattedValue: string
  change: number
  trend: 'up' | 'down'
  sparklineData: number[]
}

export const mockKPIs: KPI[] = [
  {
    id: 'calls',
    label: 'Calls',
    value: 348,
    formattedValue: '348',
    change: 6.8,
    trend: 'up',
    sparklineData: [310, 320, 325, 330, 335, 340, 348],
  },
  {
    id: 'conversations',
    label: 'Conversations',
    value: 187,
    formattedValue: '187',
    change: 4.2,
    trend: 'up',
    sparklineData: [160, 165, 170, 175, 178, 182, 187],
  },
  {
    id: 'pulls',
    label: 'Pulls',
    value: 100,
    formattedValue: '100',
    change: 9.1,
    trend: 'up',
    sparklineData: [78, 82, 85, 88, 92, 96, 100],
  },
  {
    id: 'apps',
    label: 'Apps',
    value: 63,
    formattedValue: '63',
    change: 5.0,
    trend: 'up',
    sparklineData: [50, 52, 55, 57, 59, 61, 63],
  },
  {
    id: 'submits',
    label: 'Submits',
    value: 50,
    formattedValue: '50',
    change: 8.7,
    trend: 'up',
    sparklineData: [38, 40, 42, 44, 46, 48, 50],
  },
  {
    id: 'locks',
    label: 'Locks',
    value: 38,
    formattedValue: '38',
    change: 5.6,
    trend: 'up',
    sparklineData: [28, 30, 32, 33, 35, 36, 38],
  },
  {
    id: 'funds',
    label: 'Funds',
    value: 34,
    formattedValue: '34',
    change: -3.2,
    trend: 'down',
    sparklineData: [40, 38, 37, 36, 35, 34, 34],
  },
  {
    id: 'falloutPct',
    label: 'Fallout %',
    value: 14.2,
    formattedValue: '14.2%',
    change: -1.5,
    trend: 'down',
    sparklineData: [16, 15.5, 15, 14.8, 14.5, 14.3, 14.2],
  },
  {
    id: 'pipelineValue',
    label: 'Pipeline $',
    value: 45200000,
    formattedValue: '$45.2M',
    change: 12.5,
    trend: 'up',
    sparklineData: [40, 42, 41, 43, 44, 45, 45.2],
  },
  {
    id: 'pipelineCoverage',
    label: 'Coverage',
    value: 3.2,
    formattedValue: '3.2x',
    change: 0.4,
    trend: 'up',
    sparklineData: [2.5, 2.6, 2.8, 2.9, 3.0, 3.1, 3.2],
  },
]

export const mockLoanOfficers: LoanOfficer[] = [
  {
    id: 'lo-1',
    name: 'Sarah Jenkins',
    initials: 'SJ',
    team: 'East Coast',
    five9CallsPerDay: 30,
    five9TalkTime: 145,
    ringCentralCallsPerDay: 22,
    ringCentralTalkTime: 98,
    conversations: 28,
    creditPulls: 18,
    applications: 12,
    submissions: 10,
    signed: 9,
    locks: 8,
    fundings: 7,
    pullToAppPct: 68,
    appToSubmissionPct: 85,
    submissionToLockPct: 75,
    submissionToSignedPct: 92,
    signedToFundPct: 96,
    lockedPipelineValue: 5100000,
    pipelineValue: 8500000,
    falloutPct: 8,
    followUpCompliancePct: 95,
    suspendedFilePct: 3,
    sparklineData: [60, 62, 65, 64, 66, 68, 68],
    coachingFlags: [],
  },
  {
    id: 'lo-2',
    name: 'Michael Chang',
    initials: 'MC',
    team: 'West Coast',
    five9CallsPerDay: 15,
    five9TalkTime: 62,
    ringCentralCallsPerDay: 13,
    ringCentralTalkTime: 48,
    conversations: 14,
    creditPulls: 8,
    applications: 4,
    submissions: 3,
    signed: 2,
    locks: 2,
    fundings: 1,
    pullToAppPct: 45,
    appToSubmissionPct: 70,
    submissionToLockPct: 60,
    submissionToSignedPct: 80,
    signedToFundPct: 88,
    lockedPipelineValue: 1800000,
    pipelineValue: 4200000,
    falloutPct: 18,
    followUpCompliancePct: 70,
    suspendedFilePct: 12,
    sparklineData: [55, 50, 48, 45, 46, 44, 45],
    coachingFlags: [
      {
        id: 'f1',
        type: 'low-calls',
        description:
          'Five9 & RingCentral calls per day below target (15 + 13 = 28 < 40)',
      },
      {
        id: 'f2',
        type: 'low-pull-to-app',
        description: 'Pull to App conversion is low (45%)',
      },
    ],
  },
  {
    id: 'lo-3',
    name: 'David Rodriguez',
    initials: 'DR',
    team: 'Central',
    five9CallsPerDay: 25,
    five9TalkTime: 118,
    ringCentralCallsPerDay: 20,
    ringCentralTalkTime: 85,
    conversations: 24,
    creditPulls: 14,
    applications: 9,
    submissions: 7,
    signed: 6,
    locks: 5,
    fundings: 5,
    pullToAppPct: 62,
    appToSubmissionPct: 82,
    submissionToLockPct: 72,
    submissionToSignedPct: 88,
    signedToFundPct: 94,
    lockedPipelineValue: 3400000,
    pipelineValue: 6800000,
    falloutPct: 12,
    followUpCompliancePct: 88,
    suspendedFilePct: 6,
    sparklineData: [58, 59, 60, 61, 62, 61, 62],
    coachingFlags: [],
  },
  {
    id: 'lo-4',
    name: 'Emily Chen',
    initials: 'EC',
    team: 'East Coast',
    five9CallsPerDay: 20,
    five9TalkTime: 88,
    ringCentralCallsPerDay: 15,
    ringCentralTalkTime: 55,
    conversations: 18,
    creditPulls: 10,
    applications: 6,
    submissions: 4,
    signed: 3,
    locks: 3,
    fundings: 2,
    pullToAppPct: 55,
    appToSubmissionPct: 75,
    submissionToLockPct: 65,
    submissionToSignedPct: 85,
    signedToFundPct: 90,
    lockedPipelineValue: 2300000,
    pipelineValue: 5100000,
    falloutPct: 15,
    followUpCompliancePct: 82,
    suspendedFilePct: 8,
    sparklineData: [50, 52, 54, 53, 55, 54, 55],
    coachingFlags: [
      {
        id: 'f3',
        type: 'low-coverage',
        description: 'Pipeline coverage is below target',
      },
    ],
  },
  {
    id: 'lo-5',
    name: 'James Wilson',
    initials: 'JW',
    team: 'West Coast',
    five9CallsPerDay: 10,
    five9TalkTime: 35,
    ringCentralCallsPerDay: 12,
    ringCentralTalkTime: 40,
    conversations: 9,
    creditPulls: 5,
    applications: 2,
    submissions: 1,
    signed: 1,
    locks: 0,
    fundings: 0,
    pullToAppPct: 38,
    appToSubmissionPct: 55,
    submissionToLockPct: 45,
    submissionToSignedPct: 70,
    signedToFundPct: 80,
    lockedPipelineValue: 870000,
    pipelineValue: 2900000,
    falloutPct: 25,
    followUpCompliancePct: 60,
    suspendedFilePct: 18,
    sparklineData: [45, 42, 40, 38, 35, 36, 38],
    coachingFlags: [
      {
        id: 'f4',
        type: 'low-calls',
        description:
          'Five9 & RingCentral calls critically low (10 + 12 = 22 < 30)',
      },
      {
        id: 'f5',
        type: 'high-fallout',
        description: 'Fallout rate is too high (25%)',
      },
      {
        id: 'f6',
        type: 'stale-pipeline',
        description: 'Multiple files suspended over 15 days',
      },
    ],
  },
  {
    id: 'lo-6',
    name: 'Amanda Foster',
    initials: 'AF',
    team: 'Central',
    five9CallsPerDay: 28,
    five9TalkTime: 132,
    ringCentralCallsPerDay: 20,
    ringCentralTalkTime: 90,
    conversations: 26,
    creditPulls: 16,
    applications: 11,
    submissions: 9,
    signed: 8,
    locks: 7,
    fundings: 7,
    pullToAppPct: 65,
    appToSubmissionPct: 88,
    submissionToLockPct: 78,
    submissionToSignedPct: 94,
    signedToFundPct: 98,
    lockedPipelineValue: 4700000,
    pipelineValue: 7900000,
    falloutPct: 6,
    followUpCompliancePct: 96,
    suspendedFilePct: 2,
    sparklineData: [62, 64, 63, 65, 66, 65, 65],
    coachingFlags: [],
  },
  {
    id: 'lo-7',
    name: 'Robert Taylor',
    initials: 'RT',
    team: 'East Coast',
    five9CallsPerDay: 18,
    five9TalkTime: 72,
    ringCentralCallsPerDay: 14,
    ringCentralTalkTime: 52,
    conversations: 16,
    creditPulls: 9,
    applications: 5,
    submissions: 3,
    signed: 3,
    locks: 2,
    fundings: 2,
    pullToAppPct: 50,
    appToSubmissionPct: 68,
    submissionToLockPct: 58,
    submissionToSignedPct: 82,
    signedToFundPct: 86,
    lockedPipelineValue: 1900000,
    pipelineValue: 4500000,
    falloutPct: 16,
    followUpCompliancePct: 78,
    suspendedFilePct: 10,
    sparklineData: [48, 50, 49, 51, 50, 49, 50],
    coachingFlags: [
      {
        id: 'f7',
        type: 'low-pull-to-app',
        description: 'Pull to App conversion needs improvement (50%)',
      },
    ],
  },
  {
    id: 'lo-8',
    name: 'Lisa Patel',
    initials: 'LP',
    team: 'West Coast',
    five9CallsPerDay: 32,
    five9TalkTime: 158,
    ringCentralCallsPerDay: 23,
    ringCentralTalkTime: 105,
    conversations: 32,
    creditPulls: 20,
    applications: 14,
    submissions: 13,
    signed: 12,
    locks: 11,
    fundings: 10,
    pullToAppPct: 72,
    appToSubmissionPct: 90,
    submissionToLockPct: 82,
    submissionToSignedPct: 96,
    signedToFundPct: 99,
    lockedPipelineValue: 6400000,
    pipelineValue: 9200000,
    falloutPct: 4,
    followUpCompliancePct: 98,
    suspendedFilePct: 1,
    sparklineData: [68, 70, 71, 70, 72, 73, 72],
    coachingFlags: [],
  },
]
